import './GraphLegend.scss';

import { ClassificationLegend } from './ClassificationLegend';
import { COPY_SPECIFIC } from '@sonar-software/sonar-ts-constants';
import { DeferredRender } from '@ui/general';
import { error } from '@sonar-software/xuder';
import { GraphZoomButtons } from './GraphZoomButtons';
import { GroupLegend } from './GroupLegend';
import { HideDetectedButton } from './HideDetectedButton';
import { SystemOverview } from './SystemOverview';
import { When } from 'react-if';

import { addWatermark, downloadFile } from '@security/@arch/lib';
import { AppStatusSelect, AppStatusSelectValue } from '@security/AppStatusSelect';
import { Button, Modal, Row, Space } from 'antd';
import {
	DownloadOutlined,
	ExpandAltOutlined,
	FullscreenExitOutlined
} from '@ant-design/icons';
import { GraphinContext, GraphinContextType } from '@antv/graphin';
import { off, on, trigger } from '@lib/events';
import { OrganizeBySelect, OrganizeBySelectValue } from './OrganizeBySelect';
import React, { useCallback, useContext, useState } from 'react';
import { useAction, useAppNotifications, useOnMount } from '@hooks';

export interface GraphLegendProps {
	appStatus: AppStatusSelectValue;
	extra?: React.ReactNode;
	expanded: boolean;
	groupIds: Array<string>;
	hideDetected: boolean;
	onAppStatusChange: (value: AppStatusSelectValue) => void;
	onHideDetectedChange: (value: boolean) => void;
	onOrganizeByChange: (value: OrganizeBySelectValue) => void;
	organizeBy: OrganizeBySelectValue;
}

const closeOverviewEvent = 'system_overview_close';

export const GraphLegend: React.FC<GraphLegendProps> = ({
	appStatus,
	extra,
	expanded,
	groupIds,
	hideDetected,
	onAppStatusChange,
	onHideDetectedChange,
	onOrganizeByChange,
	organizeBy
}) => {
	const { graph } = useContext<GraphinContextType>(GraphinContext);
	const logError = useAction(error);
	const { errorNotification } = useAppNotifications();

	const [ isExpanded, setIsExpanded ] = useState(false);

	useOnMount(() => {
		const handler = () => {
			setIsExpanded(false);
		};

		on(closeOverviewEvent, handler);

		return () => off(closeOverviewEvent, handler);
	});

	const handleDownloadGraph = useCallback(() => {
		graph.toFullDataURL(async(res: string) => {
			try {
				const blob = await addWatermark(res);

				if (blob) {
					downloadFile(blob);
				} else {
					throw new Error('Unable to download file.');
				}
			} catch (ex) {
				logError(ex);
				errorNotification('error');
			}
		}, 'image/png', {
			backgroundColor: '#fff',
			padding: 125
		});
	}, [ errorNotification, graph, logError ]);

	return (
		<div className='app-overview-legend'>
			<Space
				direction='vertical'
				style={ { width: '100%' } }>
				<Row
					align='bottom'
					justify='end'>
					<Space size='small'>
						<GraphZoomButtons />
						<Button
							icon={ <DownloadOutlined /> }
							id='download-graph-button'
							onClick={ handleDownloadGraph } />
						<Button
							icon={ expanded ? <FullscreenExitOutlined /> : <ExpandAltOutlined /> }
							id='expand-graph-button'
							onClick={ () => {
								if (expanded) {
									trigger(closeOverviewEvent);
								} else {
									setIsExpanded(!isExpanded);
								}
							} } />
					</Space>
				</Row>
				<Row
					align='bottom'>
					<Space
						className='controls'
						direction='vertical'>
						<div className='controls-header'>{ COPY_SPECIFIC.CONTROLS }</div>
						<Space>
							<HideDetectedButton
								hideDetected={ hideDetected }
								onHideDetectedChange={ onHideDetectedChange } />
							<AppStatusSelect
								onChange={ onAppStatusChange }
								value={ appStatus } />
						</Space>
					</Space>
				</Row>
				<Row
					align='bottom'>
					<Space
						className='groups'
						direction='vertical'>
						<div className='groups-header'>{ COPY_SPECIFIC.GROUPS }</div>
						<OrganizeBySelect
							onChange={ onOrganizeByChange }
							value={ organizeBy } />
						<When condition={ organizeBy === 'group' }>
							<GroupLegend groupIds={ groupIds } />
						</When>
						<When condition={ organizeBy === 'classification' }>
							<ClassificationLegend groupIds={ groupIds } />
						</When>
						{ extra }
					</Space>
				</Row>
			</Space>
			<Modal
				centered
				className='system-overview-modal'
				destroyOnHidden
				footer={ null }
				keyboard
				maskClosable
				onCancel={ () => setIsExpanded(false) }
				open={ isExpanded }>
				<DeferredRender delay={ 200 }>
					<SystemOverview expanded />
				</DeferredRender>
			</Modal>
		</div>
	);
};
